import { useState, useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { globalLenis } from '../index'

gsap.registerPlugin(ScrollToPlugin)

// Global audio instance va state
export let globalAudio = null
export let globalAudioStarted = false

// GSAP tween referensi - fade ni to'xtatish uchun
let audioTween = null

// Audio ni tashqaridan ishga tushirish funksiyasi - GSAP fade in
export function startGlobalAudio() {
  if (globalAudio && !globalAudioStarted) {
    globalAudioStarted = true

    // Agar oldingi tween hali ishlayotgan bo'lsa, to'xtatish
    if (audioTween) audioTween.kill()

    globalAudio.volume = 0

    return globalAudio.play()
      .then(() => {
        // GSAP bilan smooth fade in - 2 sekund
        audioTween = gsap.to(globalAudio, {
          volume: 0.3,
          duration: 2,
          ease: "power2.out"
        })
      })
      .catch(() => {
        globalAudioStarted = false
      })
  }
  return Promise.resolve()
}

// Audio ni to'xtatish - GSAP fade out
export function stopGlobalAudio() {
  if (globalAudio && globalAudioStarted) {
    if (audioTween) audioTween.kill()

    audioTween = gsap.to(globalAudio, {
      volume: 0,
      duration: 1.5,
      ease: "power2.in",
      onComplete: () => {
        globalAudio.pause()
        globalAudioStarted = false
      }
    })
  }
}

// Animatsiya variantlari
const menuSlide = {
  initial: { x: 'calc(100% + 100px)' },
  enter: { x: '0', transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  exit: { x: 'calc(100% + 100px)', transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
}

const linkSlide = {
  initial: { x: 80 },
  enter: (i) => ({ x: 0, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.05 * i } }),
  exit: (i) => ({ x: 80, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.05 * i } }),
}

const indicatorScale = {
  open: { scale: 1, transition: { duration: 0.3 } },
  closed: { scale: 0, transition: { duration: 0.4 } },
}

// Nav elementlari
const navItems = [
  { title: 'Home', href: '#home' },
  { title: 'About', href: '#about' },
  { title: 'Work', href: '#work' },
  { title: 'Contact', href: '#contact' },
]

// Sound Button - Aether uslubida chiroyli animatsiya
function SoundButton() {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Global audio reference ni saqlash
  useEffect(() => {
    if (audioRef.current) {
      globalAudio = audioRef.current
    }
  }, [])

  // globalAudioStarted o'zgarganini kuzatish
  useEffect(() => {
    const checkAudioState = () => {
      if (globalAudioStarted && audioRef.current && !audioRef.current.paused) {
        setIsPlaying(true)
      }
    }

    // Audio play event'ini tinglash
    const audio = audioRef.current
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    if (audio) {
      audio.addEventListener('play', handlePlay)
      audio.addEventListener('pause', handlePause)
    }

    // Har 100ms tekshirish (Loader'dan ishga tushirilganda)
    const interval = setInterval(checkAudioState, 100)

    return () => {
      clearInterval(interval)
      if (audio) {
        audio.removeEventListener('play', handlePlay)
        audio.removeEventListener('pause', handlePause)
      }
    }
  }, [])

  const toggleMusic = () => {
    const audio = audioRef.current
    if (!audio) return

    // Oldingi tween ni to'xtatish
    if (audioTween) audioTween.kill()

    if (!isPlaying) {
      // GSAP Fade in
      audio.volume = 0
      audio.play().then(() => {
        setIsPlaying(true)
        globalAudioStarted = true

        audioTween = gsap.to(audio, {
          volume: 0.3,
          duration: 1.5,
          ease: "power2.out"
        })
      }).catch(console.error)
    } else {
      // GSAP Fade out
      audioTween = gsap.to(audio, {
        volume: 0,
        duration: 1,
        ease: "power2.in",
        onComplete: () => {
          audio.pause()
          setIsPlaying(false)
          globalAudioStarted = false
        }
      })
    }
  }

  const bars = [0, 1, 2, 3]

  return (
    <>
      <audio ref={audioRef} src="/music.mp3" preload="auto" loop />
      <motion.button
        onClick={toggleMusic}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="sound-button"
        aria-label={isPlaying ? 'Pause music' : 'Play music'}
        whileTap={{ scale: 0.98 }}
      >
        <motion.span
          className="sound-button__text"
          animate={{ opacity: isHovered || isPlaying ? 1 : 0.5 }}
          transition={{ duration: 0.3 }}
        >
          Sound
        </motion.span>

        <div className="sound-button__bars">
          {bars.map((i) => (
            <motion.span
              key={i}
              className="sound-button__bar"
              animate={isPlaying ? {
                scaleY: [0.3, 1, 0.5, 0.8, 0.3],
                transition: {
                  duration: 1.4,
                  repeat: Infinity,
                  delay: i * 0.15,
                  ease: "easeInOut"
                }
              } : {
                scaleY: 0.3,
                transition: { duration: 0.4 }
              }}
            />
          ))}
        </div>
      </motion.button>
    </>
  )
}

// Menu Footer
function MenuFooter() {
  return (
    <div className="menu__footer">
      <a href="https://github.com/unfor-dev" target="_blank" rel="noopener noreferrer">GitHub</a>
      <a href="https://www.linkedin.com/in/unfor-dev/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
      <a href="mailto:unfordev@gmail.com">Email</a>
      <a href="https://t.me/unfor_dev" target="_blank" rel="noopener noreferrer">Telegram</a>
    </div>
  )
}

// Menu Curve
function MenuCurve() {
  const h = typeof window !== 'undefined' ? window.innerHeight : 0
  const initialPath = `M100 0 L200 0 L200 ${h} L100 ${h} Q-100 ${h / 2} 100 0`
  const targetPath = `M100 0 L200 0 L200 ${h} L100 ${h} Q100 ${h / 2} 100 0`

  const curve = {
    initial: { d: initialPath },
    enter: { d: targetPath, transition: { duration: 1, ease: [0.76, 0, 0.24, 1] } },
    exit: { d: initialPath, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  }

  return (
    <svg className="menu__curve">
      <motion.path variants={curve} initial="initial" animate="enter" exit="exit" />
    </svg>
  )
}

// Menu Item
function MenuItem({ data, isActive, setSelected, closeMenu }) {
  const { title, href, index } = data

  const handleClick = (e) => {
    e.preventDefault()
    const el = document.querySelector(href)
    if (el) {
      // GSAP bilan sekin scroll - duration 1.8s
      gsap.to(window, {
        duration: 1.8,
        scrollTo: { y: el, offsetY: 0 },
        ease: "power2.inOut"
      })
    }
    closeMenu()
  }

  return (
    <motion.div
      className="menu__item"
      onMouseEnter={() => setSelected(href)}
      custom={index}
      variants={linkSlide}
      initial="initial"
      animate="enter"
      exit="exit"
    >
      <motion.div
        className="menu__item-dot"
        variants={indicatorScale}
        animate={isActive ? 'open' : 'closed'}
      />
      <a href={href} onClick={handleClick}>{title}</a>
    </motion.div>
  )
}

// Menu Panel
function Menu({ closeMenu }) {
  const [selected, setSelected] = useState('#home')

  return (
    <motion.div
      className="menu"
      variants={menuSlide}
      initial="initial"
      animate="enter"
      exit="exit"
    >
      {/* Background text - UNFOR */}
      <motion.span
        className="menu__bg-text"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
      >
        UNFOR
      </motion.span>

      <div className="menu__body">
        <div className="menu__links">
          <p className="menu__title">Navigation</p>
          {navItems.map((item, i) => (
            <MenuItem
              key={item.href}
              data={{ ...item, index: i }}
              isActive={selected === item.href}
              setSelected={setSelected}
              closeMenu={closeMenu}
            />
          ))}
        </div>
        <MenuFooter />
      </div>
      <MenuCurve />
    </motion.div>
  )
}

// Asosiy Nav Componenti
export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false)

  // Menu ochilganda Lenis scroll'ni bloklash
  useEffect(() => {
    if (menuOpen) {
      // Lenis scroll'ni to'xtatish
      if (globalLenis) globalLenis.stop()
    } else {
      // Lenis scroll'ni qaytarish
      if (globalLenis) globalLenis.start()
    }

    // Cleanup
    return () => {
      if (globalLenis) globalLenis.start()
    }
  }, [menuOpen])

  // Menu ochilganda canvas pointer-events ni o'chirish
  useEffect(() => {
    const canvas = document.querySelector('canvas')
    if (!canvas) return

    if (menuOpen) {
      canvas.style.pointerEvents = 'none'
    } else {
      // Menu yopilganda footer ko'rinayotganini tekshirish
      const footer = document.querySelector('footer')
      if (footer) {
        const rect = footer.getBoundingClientRect()
        const isFooterVisible = rect.top < window.innerHeight && rect.bottom > 0
        if (!isFooterVisible) {
          canvas.style.pointerEvents = 'auto'
        }
      } else {
        canvas.style.pointerEvents = 'auto'
      }
    }
  }, [menuOpen])

  return (
    <>
      <header className="header">
        {/* Logo */}
        <div className="logo">
          <span className="logo__copy">©</span>
          <div className="logo__text">
            <div className="logo__stack">
              <div className="logo__row">
                <span className="logo__code">Code by</span>
                <span className="logo__name">Unfor</span>
              </div>
              <div className="logo__row">
                <span className="logo__name">Unfor</span>
                <span className="logo__surname">Dev</span>
              </div>
            </div>
          </div>
        </div>

        {/* O'ng tomon: Sound + Burger */}
        <div className="header__actions">
          <SoundButton />
          <button
            className={`burger ${menuOpen ? 'burger--active' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="burger__line" />
            <span className="burger__line" />
          </button>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {menuOpen && <Menu closeMenu={() => setMenuOpen(false)} />}
      </AnimatePresence>
    </>
  )
}
